import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useIdleTimer } from 'react-idle-timer';
import { useLocalStorage } from 'usehooks-ts';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { User as AuthConfigUser } from '@/client/types/AuthConfig';
import { useAuthStore } from '@/client/services/state/authStore';
import { useConfigStore } from '@/client/services/state/configStore';
import { usePartnerStore } from '@/client/services/state/partnerStore';
import { useEndSession } from '@/client/services/hooks/auth/useEndSession';
import v3ApiService from '@/client/services/api/clients/v3ApiClient';

// continu-api 401/403s /v3/me once it stops accepting the token (key rotated, token revoked, user
// or company suspended or gone). Keep this to /v3/me: /hub/children 403s for every non-hub company
// and content routes 403 on access rules, both for users who are still signed in.
const isRefusedSession = (error: unknown) => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  return status === 401 || status === 403;
};

export interface LogoutTimerControls {
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  activate: () => void;
  getRemainingTime: () => number;
}

/**
 * The non-visual half of an authenticated route layout: redirect-to-login,
 * the /me refresh on navigation (with the suspension / partner checks, and
 * sign-out when the api refuses the token), cross-tab token polling,
 * last-active expiry, and the idle timer that feeds LogoutTimerModal. Shared
 * by LoggedInLayout and FullScreenAdminLayout so a full-screen surface can
 * drop the nav chrome without also dropping idle logout.
 */
export const useAuthenticatedSession = () => {
  const { config } = useConfigStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authConfig, setAuthConfig, bumpActive, isLastActiveNearExpiration } = useAuthStore();
  const endSession = useEndSession();
  const { partner } = usePartnerStore();
  const location = useLocation();
  const [, setRedirectLocation] = useLocalStorage<string | null>('redirectLocation', null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const {
    data: userRefreshData,
    error: userRefreshError,
    isLoading: isUserLoading,
    refetch: refetchUser,
    isFetched: isUserFetched,
  } = useQuery({
    enabled: false,
    queryKey: ['user', authConfig.user?._id],
    queryFn: () => v3ApiService.me(),
  });

  useEffect(() => {
    // Clear any existing Angular redirect locations when the logged-in user
    // changes routes or routes back into the React app from Angular.
    localStorage.removeItem('preRedirect');

    if (!authConfig.authtoken) {
      setRedirectLocation(`${location.pathname}${location.search}`);
    }
  }, [location]);

  useEffect(() => {
    if (!authConfig.company?._id) return;
    if (!authConfig.user?._id) return;
    refetchUser();
  }, [location]);

  useEffect(() => {
    if (!isUserLoading && isUserFetched && userRefreshData) {
      const userData: AuthConfigUser = userRefreshData.user;
      setAuthConfig({
        ...authConfig,
        company: userRefreshData.company,
        user: {
          ...authConfig.user, // preserve fields /me doesn't return (is_grader, is_manager, etc.)
          ...userData, // overwrite with fresh data
        },
      });

      // Verify the user's login is still appropriate for this organization.
      // Inline so it can only run after the latest user, company and partner
      // data are all in hand.
      if (userData.suspended) {
        endSession({
          show: true,
          status: 'error',
          title: t('login.userSuspended'),
        });
      } else if (userData.partner?._id !== partner?._id) {
        endSession({
          show: true,
          status: 'error',
          title: t('login.accountProblem'),
        });
      } else if (partner?._id && (!partner.active || !userData.partner?.active)) {
        endSession({
          show: true,
          status: 'error',
          title: t('login.extendAccountDisabled'),
        });
      }
    }
  }, [isUserFetched, userRefreshData]);

  useEffect(() => {
    if (!isRefusedSession(userRefreshError)) return;
    // Order matters: redirectLocation first, endSession() hard-navigates.
    setRedirectLocation(`${location.pathname}${location.search}`);
    endSession({
      show: true,
      status: 'warning',
      title: t('login.sessionEnded'),
    });
    // reason: endSession is a new function every render (so are useIntercom's and useNPS's), so
    // listing it would re-run this on every render; a new /v3/me error is the only trigger.
  }, [userRefreshError]);

  const beginLoggingOut = () => {
    const scormIframe: HTMLElement | null = document.getElementById('playeriframe');
    if (scormIframe) {
      // @ts-ignore
      scormIframe.contentWindow?.timeoutFinishScorm();
    }

    setRedirectLocation(location.pathname);
    endSession();
  };

  useEffect(() => {
    if (!authConfig.authtoken) {
      // Extend users belong on their partner's login, with its SSO options — `/login` would
      // clear the partner store and show the company's.
      navigate(partner?._id ? `/login/extend/${partner._id}` : '/login');
    }
  }, [authConfig.authtoken]);

  useEffect(() => {
    const checkAlternateLogoutInterval = setInterval(() => {
      // The token can be cleared outside this app (learn-app, another tab), so
      // read localStorage directly rather than trusting the in-memory store.
      try {
        if (!JSON.parse(localStorage.authStore).state?.authConfig?.authtoken) {
          beginLoggingOut();
        }
      } catch (e) {
        beginLoggingOut();
      }
    }, 420);
    return () => {
      clearInterval(checkAlternateLogoutInterval);
    };
  });

  useEffect(() => {
    // Whenever this view refreshes, check the last active time for expiry.
    if (isLastActiveNearExpiration(0)) {
      beginLoggingOut();
    }
  });

  const { activate, getRemainingTime } = useIdleTimer({
    onIdle: () => beginLoggingOut(),
    onAction: () => bumpActive(),
    onActive: () => bumpActive(),
    // TODO Before prompting, check whether learn-app extended last-active
    // elsewhere; if so, reset the idle timer instead of showing the modal.
    onPrompt: () => setModalOpen(true),
    promptBeforeIdle: 120 * 1000,
    timeout: config.learnAppActivityTTL,
    throttle: 500,
    crossTab: true,
  });

  const logoutTimer: LogoutTimerControls = { modalOpen, setModalOpen, activate, getRemainingTime };

  return { authtoken: authConfig.authtoken, logoutTimer };
};
